'use client';

/**
 * ExplorerCardStack — vertical stack of explorer-surface cards.
 *
 * Cards-everywhere render path for the explorer surface. Reads the
 * active mode's `explorer_cards` from the total-matrix and renders
 * the corresponding components via `EXPLORER_CARD_REGISTRY`.
 *
 * Parallel to `<InsightsPanel />`: same primitive, different surface.
 * Layout discipline is identical (1×N vertical scroll), as the
 * cards-everywhere doc specifies — surfaces differ by layout, not by
 * primitive.
 *
 * Per-platform visibility honoured via `card.platforms` (mirrors
 * `InsightsPanel`'s filter).
 *
 * @see klyde_edge/ui_config/docs/right_rail_and_canvas_ux_direction.md
 * @see klyde_edge/ui_config/total_matrix.py — EXPLORER_CARDS
 *
 * @layer presentation
 * @arch_component_type panel
 * @arch_domain explorer
 */

import { useMemo } from 'react';
import { useLayoutStore } from '@/lib/stores/layout-store';
import { useTotalMatrixStore } from '@/lib/stores/total-matrix-store';
import { useIsMobile } from '@/lib/hooks/use-is-mobile';
import { CardPlacementProvider } from '@/lib/design/card-export';
import { CardChromeProvider } from '@/lib/design/card-chrome';
import { EXPLORER_CARD_REGISTRY } from './card-registry';

export function ExplorerCardStack() {
  const mode = useLayoutStore((s) => s.activeExplorerView);
  const matrix = useTotalMatrixStore((s) => s.matrix);
  const { isMobile } = useIsMobile();
  const currentPlatform = isMobile ? 'mobile' : 'desktop';

  const visibleCards = useMemo(() => {
    const modeConfig = matrix?.[mode];
    const cards = modeConfig?.explorer_cards ?? [];
    return cards.filter((c) => {
      const platforms = c.platforms;
      if (!platforms || platforms.length === 0) return true;
      return platforms.includes(currentPlatform);
    });
  }, [mode, matrix, currentPlatform]);

  return (
    <div className="h-full overflow-y-auto">
      <div className="p-2 space-y-2">
        {visibleCards.map((c) => {
          const Component = EXPLORER_CARD_REGISTRY[c.key];
          if (!Component) {
            if (typeof window !== 'undefined') {
              // eslint-disable-next-line no-console
              console.warn(
                `[ExplorerCardStack] Unknown card key '${c.key}' — add to EXPLORER_CARD_REGISTRY`,
              );
            }
            return null;
          }
          return (
            <CardPlacementProvider
              key={c.key}
              value={{ surface: 'explorer', slot_id: c.key }}
            >
              {/* Offer the declared typography bundle to the card (mirrors
                  the canvas LayoutEngine). CardChromeProvider resolves
                  cardTypography[contentKey] from the matrix store; cards
                  that read useCardTypography() get it, literal-using
                  cards ignore it — inert for them. */}
              <CardChromeProvider contentKey={c.key}>
                <Component />
              </CardChromeProvider>
            </CardPlacementProvider>
          );
        })}
      </div>
    </div>
  );
}
